import { useState, useEffect } from 'react';
import { Award, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLoyalty = () => {
  const [settings, setSettings] = useState({
    points_per_rupee: 0.01,
    points_per_100_inr: 1,
    redemption_rate: 1,
    min_redemption_points: 100,
    bronze_threshold: 0,
    silver_threshold: 2000,
    gold_threshold: 5000,
    bronze_multiplier: 1.0,
    silver_multiplier: 1.5,
    gold_multiplier: 2.0,
    welcome_bonus: 100
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('chaleur_admin_token');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/loyalty/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching loyalty settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/loyalty/settings`, null, {
        params: settings,
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Loyalty settings updated successfully');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <div className="p-8 text-center">Loading settings...</div>;
  }

  return (
    <div className="space-y-8" data-testid="admin-loyalty">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-light mb-2">Loyalty Program Settings</h1>
          <p className="text-muted-foreground">Configure Chaleur Club rewards rules</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="rounded-none">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Earning Rules */}
        <Card className="rounded-none">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5" />
              Earning Rules
            </CardTitle>
            <CardDescription>Configure how customers earn points</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm mb-2 block">Points per ₹1 spent</Label>
              <Input
                type="number"
                step="0.01"
                value={settings.points_per_rupee}
                onChange={(e) => handleChange('points_per_rupee', parseFloat(e.target.value))}
                className="rounded-none"
              />
              <p className="text-xs text-muted-foreground mt-1">Currently: ₹1 = {settings.points_per_rupee} points (₹100 = {settings.points_per_rupee * 100} points)</p>
            </div>
            <div>
              <Label className="text-sm mb-2 block">Welcome Bonus Points</Label>
              <Input
                type="number"
                value={settings.welcome_bonus}
                onChange={(e) => handleChange('welcome_bonus', parseInt(e.target.value))}
                className="rounded-none"
              />
              <p className="text-xs text-muted-foreground mt-1">Points given on account creation</p>
            </div>
          </CardContent>
        </Card>

        {/* Redemption Rules */}
        <Card className="rounded-none">
          <CardHeader>
            <CardTitle>Redemption Rules</CardTitle>
            <CardDescription>Configure how customers redeem points</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm mb-2 block">Redemption Rate (1 point = ₹)</Label>
              <Input
                type="number"
                step="0.1"
                value={settings.redemption_rate}
                onChange={(e) => handleChange('redemption_rate', parseFloat(e.target.value))}
                className="rounded-none"
              />
              <p className="text-xs text-muted-foreground mt-1">Value of 1 point in rupees</p>
            </div>
            <div>
              <Label className="text-sm mb-2 block">Minimum Points to Redeem</Label>
              <Input
                type="number"
                value={settings.min_redemption_points}
                onChange={(e) => handleChange('min_redemption_points', parseInt(e.target.value))}
                className="rounded-none"
              />
            </div>
          </CardContent>
        </Card>

        {/* Tier Thresholds */}
        <Card className="rounded-none">
          <CardHeader>
            <CardTitle>Tier Thresholds</CardTitle>
            <CardDescription>Points required to reach each tier</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-sm mb-2 block">Bronze</Label>
                <Input
                  type="number"
                  value={settings.bronze_threshold}
                  onChange={(e) => handleChange('bronze_threshold', parseInt(e.target.value))}
                  className="rounded-none"
                  disabled
                />
              </div>
              <div>
                <Label className="text-sm mb-2 block">Silver</Label>
                <Input
                  type="number"
                  value={settings.silver_threshold}
                  onChange={(e) => handleChange('silver_threshold', parseInt(e.target.value))}
                  className="rounded-none"
                />
              </div>
              <div>
                <Label className="text-sm mb-2 block">Gold</Label>
                <Input
                  type="number"
                  value={settings.gold_threshold}
                  onChange={(e) => handleChange('gold_threshold', parseInt(e.target.value))}
                  className="rounded-none"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tier Multipliers */}
        <Card className="rounded-none">
          <CardHeader>
            <CardTitle>Tier Multipliers</CardTitle>
            <CardDescription>Points multiplier for each tier</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-sm mb-2 block">Bronze (×)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={settings.bronze_multiplier}
                  onChange={(e) => handleChange('bronze_multiplier', parseFloat(e.target.value))}
                  className="rounded-none"
                />
              </div>
              <div>
                <Label className="text-sm mb-2 block">Silver (×)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={settings.silver_multiplier}
                  onChange={(e) => handleChange('silver_multiplier', parseFloat(e.target.value))}
                  className="rounded-none"
                />
              </div>
              <div>
                <Label className="text-sm mb-2 block">Gold (×)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={settings.gold_multiplier}
                  onChange={(e) => handleChange('gold_multiplier', parseFloat(e.target.value))}
                  className="rounded-none"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Example: Gold tier with 2× multiplier earns 2 points per ₹100 instead of 1
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Preview */}
      <Card className="rounded-none bg-amber-50 border-amber-200">
        <CardHeader>
          <CardTitle>Live Preview</CardTitle>
          <CardDescription>See how points will be calculated</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-8 text-center">
            <div>
              <p className="text-sm text-muted-foreground mb-1">₹10,000 purchase (Bronze)</p>
              <p className="text-2xl font-light text-amber-700">
                {Math.floor(10000 * settings.points_per_rupee * settings.bronze_multiplier)} pts
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">₹10,000 purchase (Silver)</p>
              <p className="text-2xl font-light text-amber-700">
                {Math.floor(10000 * settings.points_per_rupee * settings.silver_multiplier)} pts
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">₹10,000 purchase (Gold)</p>
              <p className="text-2xl font-light text-amber-700">
                {Math.floor(10000 * settings.points_per_rupee * settings.gold_multiplier)} pts
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLoyalty;
